// Checkout — Stripe Payment Element flow for the consultation review fee.
// Sits between Plan tab's PAY & BEGIN ASSESSMENT button and the
// post-payment welcome screen (spec §3 Path B + §8). Mounted at
// /app/checkout/:sessionId.
//
// Backend coordination assumed (already in place on healthyhorizons.clinic):
//   • GET  /api/stripe/public-config           → { publishableKey, mode }
//   • POST /api/consumer/stripe/payment-intent → { clientSecret, paymentIntentId }
//        body: { amount, purpose: "consultation", applicationSessionId }
//
// Flow:
//   1. Mount → fetch publishable key + create PaymentIntent with the
//      computed review fee.
//   2. Render <Elements stripe={stripePromise} options={{ clientSecret, appearance }}>.
//   3. Inside, render <PaymentForm /> with <PaymentElement /> + a single
//      "PAY $X & CONTINUE →" submit button.
//   4. Submit calls stripe.confirmPayment with return_url pointing back to
//      this page with ?paid=1&pi=... — Stripe handles 3DS / SCA in-app.
//   5. On the return-from-Stripe redirect, poll /payments/status/:pi
//      (best-effort — backend may not yet expose; we treat ?paid=1 as a
//      strong-enough signal to advance to /welcome and let the welcome
//      screen render off the session document).
//   6. /welcome clears the tray and hands off to the assessment runner.
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import Logo from "@/components/Logo";
import Button from "@/components/Button";
import { Eyebrow, Headline } from "@/components/Editorial";
import { Spinner } from "@/components/Loader";
import { apiGet, apiPost } from "@/lib/api";
import { useApplicationTray } from "@/lib/applicationTray";
import { C } from "@/lib/brand";
import { fmtMoney } from "@/lib/format";
import { toast } from "@/components/Toast";

// One-time module-scope cache: loadStripe is expensive (downloads the
// stripe.js bundle) and Stripe explicitly recommends calling it ONCE per
// page load. We key by publishableKey so a backend swap test ↔ live
// would re-create the promise rather than reuse a stale one.
let stripePromiseCache = { key: null, promise: null };
function getStripePromise(publishableKey) {
  if (!publishableKey) return null;
  if (stripePromiseCache.key !== publishableKey) {
    stripePromiseCache = { key: publishableKey, promise: loadStripe(publishableKey) };
  }
  return stripePromiseCache.promise;
}

// Brand-matched Payment Element appearance. Stripe Elements respect a
// subset of CSS custom properties; we map them to VOYA9's palette so the
// checkout never feels like a foreign embed.
const stripeAppearance = {
  theme: "flat",
  variables: {
    colorPrimary: "#0F0E0D",
    colorBackground: "#FFFFFF",
    colorText: "#0F0E0D",
    colorDanger: "#C04E3A",
    fontFamily: '"DM Sans", -apple-system, BlinkMacSystemFont, sans-serif',
    fontSizeBase: "16px",
    spacingUnit: "5px",
    borderRadius: "0px",
  },
  rules: {
    ".Label": {
      fontSize: "11px",
      fontWeight: "800",
      letterSpacing: "0.2em",
      textTransform: "uppercase",
      color: "#7E756B",
    },
    ".Input": {
      border: "1.5px solid #E1DDD7",
      padding: "16px 18px",
      transition: "border-color 160ms ease",
    },
    ".Input:focus": {
      border: "1.5px solid #0F0E0D",
      boxShadow: "none",
    },
    ".Tab": {
      border: "1.5px solid #E1DDD7",
      borderRadius: "0",
    },
    ".Tab:hover": {
      border: "1.5px solid #E1DDD7",
      backgroundColor: "#F4C9A8",
      color: "#0F0E0D",
    },
    ".Tab--selected": {
      border: "0",
      backgroundColor: "#F4C9A8",
      color: "#0F0E0D",
    },
    ".Tab--selected:hover": {
      border: "0",
      backgroundColor: "#F4C9A8",
      color: "#0F0E0D",
    },
    ".Tab--selected:focus": {
      border: "0",
      boxShadow: "none",
      backgroundColor: "#F4C9A8",
      color: "#0F0E0D",
    },
    ".TabIcon--selected": {
      color: "#0F0E0D",
      fill: "#0F0E0D",
    },
    ".TabLabel--selected": {
      color: "#0F0E0D",
    },
  },
};

export default function Checkout() {
  const { sessionId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { items: trayItems } = useApplicationTray();
  const [publishableKey, setPublishableKey] = useState(null);
  const [stripeMode, setStripeMode] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [paymentIntentId, setPaymentIntentId] = useState(null);
  const [error, setError] = useState(null);
  const [retryNonce, setRetryNonce] = useState(0); // bump to force the load-effect to run again
  const [session, setSession] = useState(undefined);

  // Compute fee from tray. Backend is the source of truth (we send only
  // applicationSessionId + a hint amount — backend re-validates) so a
  // tampered local count cannot manipulate the price.
  const tieredItems = useMemo(
    () => (trayItems.length > 0
      ? trayItems
      : (session?.selectedPrograms || []).map((p) => ({
          programSlug: p.slug || p.programSlug,
          programName: p.name || p.programName,
          tierName: p.tierName || p.tier,
        }))
    ),
    [trayItems, session],
  );
  // Two pricing paths (spec Feb 2026):
  //   • Choice-led  — $49 first program + $19 per additional. Member
  //     knows what they want; tray drives the line items.
  //   • Assessment-led — $75 flat. Member said "not sure" on the
  //     pre-selection home; physician will recommend after a multi-
  //     module discovery + per-program assessment.
  // Path is locked on the session by /sessions/:id/path-type. For
  // sessions where that endpoint hasn't shipped yet, we infer from
  // applicationType === "full_assessment" so checkout still does the
  // right thing in production.
  const isAssessmentLed =
    session?.pathType === "assessment_led" ||
    session?.applicationType === "full_assessment";
  const ASSESSMENT_LED_FEE = 75;
  const choiceLedFee = tieredItems.length === 0 ? 0 : 49 + 19 * (tieredItems.length - 1);
  const fee = isAssessmentLed ? ASSESSMENT_LED_FEE : choiceLedFee;

  // Stripe API expects amount in cents (smallest currency unit).
  const amountCents = Math.round(fee * 100);

  // Re-entrant after Stripe redirect — if we land here with ?paid=1,
  // hand off to /welcome (the post-payment confirmation screen).
  const paid = searchParams.get("paid") === "1";

  useEffect(() => {
    if (paid) {
      navigate(`/app/assessment/${sessionId}/welcome`, { replace: true });
    }
  }, [paid, sessionId, navigate]);

  useEffect(() => {
    if (paid) return;
    let cancelled = false;
    (async () => {
      // Network errors get one silent retry — many "Load failed" reports
      // are transient (mobile-Safari proxy hiccup, Cloudflare cold-edge,
      // a JS extension racing). Anything else fails fast.
      const fetchWithRetry = async (path, opts = {}) => {
        try {
          return await apiGet(path, opts);
        } catch (err) {
          const raw = err?.detail || err?.message || "";
          const isNetwork = /load failed|failed to fetch|network/i.test(raw) || err?.status === 0 || !err?.status;
          if (!isNetwork) throw err;
          // 350ms backoff is enough for transient edge issues without
          // making the user wait noticeably.
          await new Promise((r) => setTimeout(r, 350));
          return apiGet(path, opts);
        }
      };

      try {
        // Hit the two endpoints in series so we know exactly which one
        // failed (and can show better diagnostics). Public-config first
        // because it's tiny and gates everything else.
        const cfg = await fetchWithRetry("/stripe/public-config");
        if (cancelled) return;
        if (!cfg?.publishableKey) {
          throw new Error("Stripe is not configured. Please contact support.");
        }
        setPublishableKey(cfg.publishableKey);
        setStripeMode(cfg.mode || (cfg.publishableKey.startsWith("pk_test") ? "test" : "live"));

        // Session is optional — many flows work without it, so swallow
        // its errors silently after the retry.
        const sess = await fetchWithRetry(`/consumer/assessment/sessions/${sessionId}`).catch(() => null);
        if (cancelled) return;
        setSession(sess);
      } catch (err) {
        if (!cancelled) {
          const raw = err?.detail || err?.message || "";
          const isNetwork = /load failed|failed to fetch|network/i.test(raw) || err?.status === 0 || !err?.status;
          // Surface the diagnostic detail in devtools so a sharing user
          // can copy/paste it into a bug report.
          // eslint-disable-next-line no-console
          console.warn("[checkout] config/session load failed", { status: err?.status, detail: raw, error: err });
          setError({
            kind: isNetwork ? "network" : (err?.status >= 500 ? "server" : "other"),
            message: isNetwork
              ? "We couldn't reach our server. Try again — and if it keeps happening, an ad-blocker or VPN may be blocking us."
              : (err?.status >= 500
                  ? "Something went wrong on our side. Try again in a moment."
                  : (raw || "Couldn't load checkout.")),
            at: "config-and-session",
          });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [paid, sessionId, retryNonce]);

  // Once we have publishable key + session, mint a PaymentIntent. We only
  // do this once per mount — the clientSecret is bound to a specific
  // amount, so any later tray changes require a fresh intent. We also
  // wait until the session document has loaded (`session !== undefined`)
  // so we don't mint a choice-led $49 intent for an assessment-led $75
  // session that has stray tray items from a previous flow.
  useEffect(() => {
    if (paid || !publishableKey || amountCents <= 0 || clientSecret) return;
    if (session === undefined) return;
    let cancelled = false;
    (async () => {
      // Same retry pattern as the config/session load above — covers
      // transient network blips without surfacing them to the user.
      const postWithRetry = async () => {
        try {
          return await apiPost("/consumer/stripe/payment-intent", {
            amount: amountCents,
            purpose: "consultation",
            applicationSessionId: sessionId,
          });
        } catch (err) {
          const raw = err?.detail || err?.message || "";
          const isNetwork = /load failed|failed to fetch|network/i.test(raw) || err?.status === 0 || !err?.status;
          if (!isNetwork) throw err;
          await new Promise((r) => setTimeout(r, 350));
          return apiPost("/consumer/stripe/payment-intent", {
            amount: amountCents,
            purpose: "consultation",
            applicationSessionId: sessionId,
          });
        }
      };

      try {
        const res = await postWithRetry();
        if (cancelled) return;
        if (!res?.clientSecret) {
          throw new Error("Couldn't initialize payment. Please try again.");
        }
        setClientSecret(res.clientSecret);
        setPaymentIntentId(res.paymentIntentId);
      } catch (err) {
        if (!cancelled) {
          const raw = err?.detail || err?.message || "";
          const isNetwork = /load failed|failed to fetch|network/i.test(raw) || err?.status === 0 || !err?.status;
          // eslint-disable-next-line no-console
          console.warn("[checkout] payment-intent failed", { status: err?.status, detail: raw, error: err });
          setError({
            kind: isNetwork ? "network" : (err?.status >= 500 ? "server" : "other"),
            message: isNetwork
              ? "We couldn't reach our payment server. Try again — and if it keeps happening, an ad-blocker or VPN may be blocking us."
              : (err?.status >= 500
                  ? "Stripe is having a moment. Try again in 30 seconds."
                  : (raw || "Couldn't start payment.")),
            at: "payment-intent",
          });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [paid, publishableKey, amountCents, clientSecret, sessionId, session, retryNonce]);

  if (paid) {
    // Redirecting — don't flash the form.
    return <FullScreen><Spinner /></FullScreen>;
  }
  if (error) {
    const retry = () => {
      setError(null);
      // Bump the nonce so the load-effects re-run even though all the
      // other deps are the same.
      setRetryNonce((n) => n + 1);
    };
    return (
      <FullScreen>
        <Eyebrow color={C.coral}>★ HMM</Eyebrow>
        <Headline before="WE COULDN'T" italic="start checkout." size={48} className="mt-4" />
        <p style={{ marginTop: 18, fontSize: 15, color: C.warmGray, maxWidth: 520, lineHeight: 1.55 }}>
          {error.message}
        </p>
        {error.kind === "network" && (
          <p
            data-testid="checkout-network-hint"
            style={{
              marginTop: 10,
              fontSize: 13,
              color: C.warmGray,
              fontFamily: '"Instrument Serif", Georgia, serif',
              fontStyle: "italic",
              maxWidth: 520,
              lineHeight: 1.55,
            }}
          >
            Quick checks — turn off any VPN or ad blocker, then tap retry. Your selections are still saved on your plan.
          </p>
        )}
        <div style={{ marginTop: 24, display: "flex", gap: 12, flexWrap: "wrap" }}>
          <Button variant="primary" testId="checkout-error-retry" onClick={retry}>
            TRY AGAIN →
          </Button>
          <Button variant="ghost" testId="checkout-error-back" onClick={() => navigate("/app/plan")}>
            BACK TO PLAN
          </Button>
        </div>
      </FullScreen>
    );
  }
  if (!publishableKey || !clientSecret) {
    return <FullScreen><Spinner /></FullScreen>;
  }

  const stripePromise = getStripePromise(publishableKey);

  return (
    <div data-testid="checkout-page" style={{ minHeight: "100vh", background: C.cream, paddingBottom: 80 }}>
      <header style={{ background: C.ink, padding: "18px 22px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Logo light size={20} />
        <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.2em", color: C.peach }}>★ STEP 1 OF 2 · PAYMENT</span>
      </header>

      <section style={{ padding: "56px 24px 24px", maxWidth: 720, margin: "0 auto" }}>
        <Eyebrow color={C.peachDeep}>
          ★ {isAssessmentLed ? "ASSESSMENT-LED REVIEW" : "REVIEW FEE"}
        </Eyebrow>
        {isAssessmentLed ? (
          <>
            <Headline before="ONE FEE." italic="any program a physician recommends." size={56} className="mt-4" />
            <p style={{ marginTop: 16, fontSize: 15, color: C.warmGray, lineHeight: 1.6, maxWidth: 560 }}>
              You'll move through a multi-module discovery + per-program assessment.
              A licensed physician reviews your responses and recommends the right program — or tells you VOYA9 isn't the answer.
              {stripeMode === "test" && " (Test mode — use card 4242 4242 4242 4242, any future expiry, any 3-digit CVC.)"}
            </p>
          </>
        ) : (
          <>
            <Headline before="ONE FEE." italic="all selected programs." size={56} className="mt-4" />
            <p style={{ marginTop: 16, fontSize: 15, color: C.warmGray, lineHeight: 1.6, maxWidth: 560 }}>
              A licensed physician will review your assessment and design a personalized protocol for each program you selected.
              {stripeMode === "test" && " (Test mode — use card 4242 4242 4242 4242, any future expiry, any 3-digit CVC.)"}
            </p>
          </>
        )}
      </section>

      <section style={{ padding: "0 24px", maxWidth: 720, margin: "0 auto" }}>
        <FeeBreakdown items={tieredItems} fee={fee} isAssessmentLed={isAssessmentLed} />
      </section>

      <section style={{ padding: "32px 24px 0", maxWidth: 720, margin: "0 auto" }}>
        <Elements
          stripe={stripePromise}
          options={{ clientSecret, appearance: stripeAppearance }}
        >
          <PaymentForm
            sessionId={sessionId}
            paymentIntentId={paymentIntentId}
            fee={fee}
            stripeMode={stripeMode}
          />
        </Elements>
      </section>
    </div>
  );
}

function FeeBreakdown({ items, fee, isAssessmentLed }) {
  if (isAssessmentLed) {
    return (
      <div
        data-testid="checkout-fee-breakdown"
        data-path-type="assessment_led"
        style={{ background: C.peach, color: C.peachDark, padding: "24px 22px" }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.2em", color: C.peachDark }}>
            ★ ASSESSMENT-LED PATH
          </span>
          <span style={{ fontSize: 16, fontWeight: 700, color: C.ink, lineHeight: 1.4 }}>
            Multi-module discovery + per-program assessment + physician recommendation.
          </span>
        </div>
        <div
          style={{
            marginTop: 18, paddingTop: 14,
            borderTop: `1.5px solid ${C.peachDark}33`,
            display: "flex", justifyContent: "space-between", alignItems: "baseline",
            flexWrap: "wrap", gap: 8,
          }}
        >
          <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.2em", color: C.peachDark }}>★ TOTAL TODAY</span>
          <span data-testid="checkout-total" style={{ fontSize: 28, fontWeight: 800, color: C.ink, letterSpacing: "-0.02em" }}>
            {fmtMoney(fee)}
          </span>
        </div>
        <p style={{ marginTop: 10, fontSize: 12, color: C.peachDark, lineHeight: 1.5 }}>
          Flat $75 covers the full review regardless of how many programs the physician recommends. Monthly subscriptions activate separately if you accept.
        </p>
      </div>
    );
  }
  return (
    <div
      data-testid="checkout-fee-breakdown"
      data-path-type="choice_led"
      style={{ background: C.peach, color: C.peachDark, padding: "24px 22px" }}
    >
      {items.length > 0 && (
        <ul data-testid="checkout-program-list" style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 6 }}>
          {items.map((it, i) => (
            <li key={`${it.programSlug || i}`} style={{ fontSize: 14, color: C.ink, lineHeight: 1.45 }}>
              <strong style={{ fontWeight: 800 }}>{it.programName}</strong>
              {it.tierName && <span style={{ color: C.peachDark }}> — {it.tierName} tier</span>}
            </li>
          ))}
        </ul>
      )}
      <div style={{ marginTop: items.length > 0 ? 18 : 0, paddingTop: items.length > 0 ? 14 : 0, borderTop: items.length > 0 ? `1.5px solid ${C.peachDark}33` : "none", display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 8 }}>
        <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.2em", color: C.peachDark }}>★ TOTAL TODAY</span>
        <span data-testid="checkout-total" style={{ fontSize: 28, fontWeight: 800, color: C.ink, letterSpacing: "-0.02em" }}>
          {fmtMoney(fee)}
        </span>
      </div>
      {items.length > 1 && (
        <p style={{ marginTop: 10, fontSize: 12, color: C.peachDark, lineHeight: 1.5 }}>
          $49 first program + $19 × {items.length - 1} additional. After approval, monthly subscriptions activate separately.
        </p>
      )}
    </div>
  );
}

function PaymentForm({ sessionId, paymentIntentId, fee, stripeMode }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || submitting) return;
    setSubmitting(true);
    setFormError(null);

    // Stripe.js handles 3DS / SCA / redirect-based methods inside this
    // call. We pass return_url so any wallet/Klarna/redirect method comes
    // back to /app/checkout/:sessionId?paid=1&pi=...; the useEffect at
    // the top of the page handles the post-redirect handoff.
    const returnUrl = `${window.location.origin}/app/checkout/${sessionId}?paid=1&pi=${paymentIntentId}`;
    const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: returnUrl },
      redirect: "if_required",
    });

    if (stripeError) {
      setFormError(stripeError.message || "Payment failed. Please try a different card.");
      setSubmitting(false);
      return;
    }

    if (paymentIntent?.status === "succeeded") {
      // Best-effort: tell the backend the intent succeeded so it can
      // advance the session immediately rather than waiting for the
      // webhook. Silent fall-through if the endpoint isn't implemented;
      // backend webhook will eventually catch up.
      try {
        await apiPost(`/consumer/assessment/sessions/${sessionId}/payment-confirmed`, {
          paymentIntentId,
        });
      } catch (_) { /* fall through */ }
      window.location.href = `/app/assessment/${sessionId}/welcome?paid=1`;
      return;
    }

    if (paymentIntent?.status === "processing") {
      toast("Payment is processing — we'll email you shortly.", "info");
      window.location.href = `/app/assessment/${sessionId}/welcome?paid=1`;
      return;
    }

    // Anything else (requires_action that wasn't handled, etc.) — let the
    // user retry.
    setFormError("Payment didn't complete. Please try again.");
    setSubmitting(false);
  };

  return (
    <form onSubmit={submit} data-testid="checkout-form">
      <PaymentElement options={{ layout: "tabs" }} />
      {formError && (
        <p data-testid="checkout-error" style={{ marginTop: 14, fontSize: 13, color: C.coral, lineHeight: 1.5 }}>
          {formError}
        </p>
      )}
      <div style={{ marginTop: 28 }}>
        <Button
          type="submit"
          variant="primary"
          size="lg"
          block
          testId="checkout-submit-btn"
          disabled={!stripe || !elements || submitting}
        >
          {submitting ? "PROCESSING…" : `PAY ${fmtMoney(fee)} & CONTINUE →`}
        </Button>
      </div>
      <p style={{ marginTop: 14, fontSize: 12, color: C.warmGray, textAlign: "center", lineHeight: 1.5 }}>
        Powered by Stripe. {stripeMode === "test" ? "Test mode — no real charge." : "Your card is charged immediately."}
      </p>
    </form>
  );
}

function FullScreen({ children }) {
  return (
    <div style={{ minHeight: "100vh", background: C.cream, padding: 64, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", textAlign: "center" }}>
      {children}
    </div>
  );
}
